import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, FunnelIcon, XMarkIcon, UserIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../utils/api';
import { Asset, AssetCategory, AssetStatus, Employee } from '../../types/api';

const AssetList: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterCategory, setFilterCategory] = useState<AssetCategory | ''>('');
  const [filterStatus, setFilterStatus] = useState<AssetStatus | ''>('');
  const [filterAssignee, setFilterAssignee] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);

  const isReadOnly = user?.role === 'user';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (selectedLocation?.id) {
      loadAssets();
      loadEmployees();
    } else {
      setAssets([]);
      setLoading(false);
    }
  }, [selectedLocation?.id, filterCategory, filterStatus, filterAssignee]);

  const loadEmployees = async () => {
    if (!selectedLocation?.id) return;
    try {
      // Fetch employees from payroll system - only managers and regular farm labour
      const response = await apiClient.get(`/employees?location_id=${selectedLocation.id}&is_active=true`);
      // Filter to only show managers and regular farm labour (exclude daily wage labour)
      const filteredEmployees = (response.data || []).filter((emp: Employee) => 
        emp.employee_type === 'manager' || emp.employee_type === 'regular_farm_labour'
      );
      setEmployees(filteredEmployees);
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  const loadAssets = async () => {
    if (!selectedLocation?.id) {
      setError('');
      setAssets([]);
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({ location_id: selectedLocation.id });
      if (filterCategory) params.append('category', filterCategory);
      if (filterStatus) params.append('status', filterStatus);
      if (filterAssignee && filterAssignee !== 'unassigned') {
        params.append('assigned_to_employee_id', filterAssignee);
      }
      
      const response = await apiClient.get(`/assets?${params.toString()}`);
      setAssets(response.data || []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (assetId: string) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) {
      return;
    }
    
    try {
      setDeletingId(assetId);
      await apiClient.delete(`/assets/${assetId}`);
      loadAssets();
    } catch (err) {
      alert(getErrorMessage(err));
    } finally {
      setDeletingId(null);
    }
  };

  const handleAssign = async (assetId: string) => {
    // Show a simple dropdown for employee selection
    const selectedEmployeeId = prompt('Enter employee ID to assign (or leave empty to unassign):\n\nAvailable employees:\n' + 
      employees.map(e => `${e.id}: ${e.name} (${e.employee_code})`).join('\n'));
    if (selectedEmployeeId === null) return;
    
    try {
      if (selectedEmployeeId.trim()) {
        await apiClient.post(`/assets/${assetId}/assign?employee_id=${selectedEmployeeId.trim()}`);
      } else {
        await apiClient.post(`/assets/${assetId}/unassign`);
      }
      loadAssets();
    } catch (err) {
      alert(getErrorMessage(err));
    }
  };

  const handleExportCSV = () => {
    const headers = ['Asset Code', 'Name', 'Category', 'Purchase Date', 'Purchase Price', 'Current Value', 'Status', 'Assigned To'];
    const rows = filteredAssets.map(asset => [
      asset.asset_code,
      asset.name,
      asset.category,
      asset.purchase_date,
      asset.purchase_price.toString(),
      asset.current_value?.toString() || '',
      asset.status,
      asset.assigned_to_employee_name || 'Unassigned'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assets-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Filter assets by search term and unassigned filter
  const filteredAssets = assets.filter(asset => {
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      if (!asset.name.toLowerCase().includes(term) && !asset.asset_code.toLowerCase().includes(term)) {
        return false;
      }
    }
    
    // Unassigned filter
    if (filterAssignee === 'unassigned') {
      return !asset.assigned_to_employee_id;
    }
    
    return true;
  });

  const getStatusBadgeColor = (status: AssetStatus) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      case 'disposed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryLabel = (category: AssetCategory) => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  if (loading && assets.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Assets</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage physical assets at {selectedLocation?.name || 'your location'}
            </p>
          </div>
          {!isReadOnly && (
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              {isAdmin && (
                <button
                  onClick={handleExportCSV}
                  className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100"
                >
                  Export CSV
                </button>
              )}
              <Link
                to="/assets/new"
                className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:bg-blue-800"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Asset
              </Link>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name or asset code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100"
          >
            <FunnelIcon className="h-5 w-5 mr-2" />
            Filters
            {showFilters ? <XMarkIcon className="h-5 w-5 ml-2" /> : null}
          </button>
        </div>

        {showFilters && (
          <div className="p-4 bg-gray-50 rounded-lg space-y-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Category
              </label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value as AssetCategory | '')}
                className="w-full px-3 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Categories</option>
                <option value="equipment">Equipment</option>
                <option value="vehicle">Vehicle</option>
                <option value="building">Building</option>
                <option value="tool">Tool</option>
                <option value="machinery">Machinery</option>
                <option value="electronics">Electronics</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as AssetStatus | '')}
                className="w-full px-3 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="maintenance">Maintenance</option>
                <option value="disposed">Disposed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Assigned To
              </label>
              <select
                value={filterAssignee}
                onChange={(e) => setFilterAssignee(e.target.value)}
                className="w-full px-3 py-2.5 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Assignees</option>
                <option value="unassigned">Unassigned</option>
                {employees.map(employee => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name} ({employee.employee_code})
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Assets Table */}
      {filteredAssets.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">No assets found</p>
          {!isReadOnly && (
            <Link
              to="/assets/new"
              className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Create your first asset
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="block md:hidden space-y-3">
            {filteredAssets.map((asset) => (
              <div key={asset.id} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">{asset.name}</h3>
                    <p className="text-xs text-gray-500 font-mono">{asset.asset_code}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(asset.status)}`}>
                    {asset.status.charAt(0).toUpperCase() + asset.status.slice(1)}
                  </span>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Category:</span>
                    <span className="text-gray-900 font-medium">{getCategoryLabel(asset.category)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Purchase Date:</span>
                    <span className="text-gray-900">{new Date(asset.purchase_date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Purchase Price:</span>
                    <span className="text-gray-900 font-medium">₹{asset.purchase_price.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Current Value:</span>
                    <span className="text-gray-900 font-medium">
                      {asset.current_value ? `₹${asset.current_value.toLocaleString()}` : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Assigned To:</span>
                    <span className="text-gray-900">
                      {asset.assigned_to_employee_name || (
                        <span className="text-gray-400 italic">Unassigned</span>
                      )}
                    </span>
                  </div>
                </div>

                {isAdmin && (
                  <div className="mt-4 pt-3 border-t border-gray-200 flex items-center justify-end gap-3">
                    <button
                      onClick={() => navigate(`/assets/${asset.id}/edit`)}
                      className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg"
                      title="Edit"
                    >
                      <PencilIcon className="h-4 w-4" />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleAssign(asset.id)}
                      className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg"
                      title="Assign"
                    >
                      <UserIcon className="h-4 w-4" />
                      <span>Assign</span>
                    </button>
                    <button
                      onClick={() => handleDelete(asset.id)}
                      disabled={deletingId === asset.id}
                      className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Asset Code
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Purchase Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Purchase Price
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Current Value
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned To (Employee)
                    </th>
                    {isAdmin && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAssets.map((asset) => (
                    <tr key={asset.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {asset.asset_code}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {asset.name}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {getCategoryLabel(asset.category)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(asset.purchase_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        ₹{asset.purchase_price.toLocaleString()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {asset.current_value ? `₹${asset.current_value.toLocaleString()}` : '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(asset.status)}`}>
                          {asset.status.charAt(0).toUpperCase() + asset.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {asset.assigned_to_employee_name || (
                          <span className="text-gray-400 italic">Unassigned</span>
                        )}
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/assets/${asset.id}/edit`)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleAssign(asset.id)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Assign"
                            >
                              <UserIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(asset.id)}
                              disabled={deletingId === asset.id}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                              title="Delete"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AssetList;

